// File: frontend/src/components/admin/DashboardRecentSection.tsx

import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaUserPlus, FaPen, FaFutbol, FaBuilding, FaShieldAlt, FaRunning, FaHashtag, FaCircle } from "react-icons/fa";
import { DashboardStats } from "../../services/adminDashboardService";
import { generateProfileUrl, generateMatchUrlFromEntry } from "../../utils/slugGenerator";

function formatDashboardDateTime(iso: string): string {
  const d = new Date(iso);
  return (
    d.toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" }) +
    " " +
    d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })
  );
}

interface Props {
  stats: DashboardStats | null;
}

export const DashboardRecentSection: React.FC<Props> = ({ stats }) => {
  const { t } = useTranslation();

  return (
    <div className="dash-recent-row">
      {/* Recently online users — takes the slot the "recently registered" panel
          used to occupy. Same table format; the date column shows the last
          login instead of the registration date, and users with a live
          WebSocket session are marked with a green dot and listed first. */}
      <div className="dash-recent-panel">
        <div className="dash-recent-header">
          <FaCircle size={11} />
          {t("dash_recent_online_title")}
        </div>
        <table className="dash-recent-table">
          <thead>
            <tr>
              <th>{t("dash_recent_col_user")}</th>
              <th>{t("dash_recent_col_role")}</th>
              <th>{t("dash_recent_col_last_seen")}</th>
            </tr>
          </thead>
          <tbody>
            {stats?.recentOnlineUsers.length ? (
              stats.recentOnlineUsers.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.online && (
                      <FaCircle
                        size={8}
                        title={t("dash_recent_online_now")}
                        style={{ color: "var(--color-success)", marginRight: 6, verticalAlign: "middle" }}
                      />
                    )}
                    <Link to={generateProfileUrl(u.username, u.id)} className="dash-user-link">
                      @{u.username}
                    </Link>
                  </td>
                  <td><span className={`dash-role-badge ${u.role}`}>{t(`user_role_${u.role.toLowerCase()}`, u.role)}</span></td>
                  <td>{formatDashboardDateTime(u.lastLoginDate)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", color: "var(--text-muted)" }}>
                  {t("dash_no_data")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="dash-recent-panel">
        <div className="dash-recent-header">
          <FaPen size={13} />
          {t("dash_recent_entries_title")}
        </div>
        <ul className="dash-entry-list">
          {stats?.recentEntries.length ? (
            stats.recentEntries.map((e) => {
              const matchUrl  = e.matchId && e.matchDate
                ? generateMatchUrlFromEntry(e.homeTeam, e.awayTeam, new Date(e.matchDate + "T00:00:00"), e.matchId)
                : null;
              const clubUrl   = e.clubSlug ? `/kulup/${e.clubSlug}` : null;
              const teamUrl   = e.teamClubSlug && e.teamSportSlug
                ? `/takim/${e.teamClubSlug}/${e.teamSportSlug}`
                : null;
              const personUrl = e.personSlug ? `/kisi/${e.personSlug}` : null;
              const topicUrl  = e.topicSlug ? `/gundem/${e.topicSlug}` : null;
              const pageUrl   = matchUrl ?? clubUrl ?? teamUrl ?? personUrl ?? topicUrl ?? null;
              const entryUrl  = pageUrl ? `${pageUrl}#entry-${e.id}` : null;
              const subjectLabel = (e.homeTeam && e.awayTeam)
                ? `${e.homeTeam} - ${e.awayTeam}`
                : e.clubLabel ?? e.teamLabel ?? e.personLabel ?? e.topicLabel ?? "-";

              return (
                <li key={e.id} className="dash-entry-item">
                  {entryUrl ? (
                    <Link to={entryUrl} className="dash-entry-preview">{e.contentPreview}</Link>
                  ) : (
                    <span className="dash-entry-preview">{e.contentPreview}</span>
                  )}
                  <span className="dash-entry-meta">
                    <Link to={generateProfileUrl(e.authorUsername, e.authorId)} className="dash-entry-author">
                      @{e.authorUsername}
                    </Link>
                    <span className="dash-entry-separator">·</span>
                    {pageUrl ? (
                      <Link to={pageUrl} className="dash-entry-subject-link">
                        {matchUrl  && <FaFutbol    size={10} className="dash-entry-subject-icon" />}
                        {clubUrl   && <FaBuilding  size={10} className="dash-entry-subject-icon" />}
                        {teamUrl   && <FaShieldAlt size={10} className="dash-entry-subject-icon" />}
                        {personUrl && <FaRunning   size={10} className="dash-entry-subject-icon" />}
                        {topicUrl  && <FaHashtag   size={10} className="dash-entry-subject-icon" />}
                        {subjectLabel}
                      </Link>
                    ) : (
                      <span>{subjectLabel}</span>
                    )}
                    <span className="dash-entry-separator">·</span>
                    <span>{formatDashboardDateTime(e.createdAt)}</span>
                  </span>
                </li>
              );
            })
          ) : (
            <li className="dash-entry-item" style={{ color: "var(--text-muted)", textAlign: "center" }}>
              {t("dash_no_data")}
            </li>
          )}
        </ul>
      </div>

      {/* Recently registered users — moved down: the grid wraps this third panel
          onto the row below the online / entries pair. */}
      <div className="dash-recent-panel">
        <div className="dash-recent-header">
          <FaUserPlus size={14} />
          {t("dash_recent_users_title")}
        </div>
        <table className="dash-recent-table">
          <thead>
            <tr>
              <th>{t("dash_recent_col_user")}</th>
              <th>{t("dash_recent_col_role")}</th>
              <th>{t("dash_recent_col_date")}</th>
            </tr>
          </thead>
          <tbody>
            {stats?.recentUsers.length ? (
              stats.recentUsers.map((u) => (
                <tr key={u.id}>
                  <td>
                    <Link to={generateProfileUrl(u.username, u.id)} className="dash-user-link">
                      @{u.username}
                    </Link>
                  </td>
                  <td><span className={`dash-role-badge ${u.role}`}>{t(`user_role_${u.role.toLowerCase()}`, u.role)}</span></td>
                  <td>{formatDashboardDateTime(u.registrationDate)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={3} style={{ textAlign: "center", color: "var(--text-muted)" }}>
                  {t("dash_no_data")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};